import { delay, http, HttpResponse } from "msw";
import { apiPaths } from "../../api-paths.js";
import type {
  Trips_GetTripStatus_GetTripStatusQueryResponse_Success,
  Trips_Status_TripStatus,
} from "../../swagger-types/v1/index.js";

interface GetTripStatusHandlerFactoryOptions {
  delayMs?: number | "infinite";
}

const waitForFactoryDelay = async ({ delayMs }: GetTripStatusHandlerFactoryOptions = {}) => {
  if (delayMs === "infinite") {
    await delay("infinite");
    return;
  }

  if (typeof delayMs === "number" && delayMs > 0) {
    await delay(delayMs);
  }
};

const waitUntilRequestAborted = async (request: Request) => {
  await new Promise<void>((resolve) => {
    request.signal.addEventListener("abort", () => resolve(), { once: true });
  });
};

const getTripKeyFromRequest = async (request: Request): Promise<string> => {
  const body = (await request.json()) as { tripKey?: string };
  return body.tripKey ?? "fb0976c1-172b-4517-ac47-247232d39a21";
};

const createTripStatusResponse = (
  status: Trips_Status_TripStatus,
  tripKey: string,
): Trips_GetTripStatus_GetTripStatusQueryResponse_Success => {
  return {
    tripKey,
    status,
  };
};

const createGetTripStatus200Handler = (
  status: Trips_Status_TripStatus,
  options: GetTripStatusHandlerFactoryOptions = {},
) => [
  http.post(`**${apiPaths.trips.status}`, async ({ request }) => {
    await waitForFactoryDelay(options);
    const tripKey = await getTripKeyFromRequest(request);
    return HttpResponse.json(createTripStatusResponse(status, tripKey), { status: 200 });
  }),
];

const getConfirmedTripStatusHandler = createGetTripStatus200Handler("Confirmed");
const getInProcessTripStatusHandler = createGetTripStatus200Handler("InProcess");
const getInAttemptTripStatusHandler = createGetTripStatus200Handler("InAttempt");

const getConfirmedTripStatusAfterPollingHandler = [
  http.post(
    `**${apiPaths.trips.status}`,
    async ({ request }) => {
      const tripKey = await getTripKeyFromRequest(request);
      return HttpResponse.json(createTripStatusResponse("InProcess", tripKey), { status: 200 });
    },
    { once: true },
  ),
  http.post(`**${apiPaths.trips.status}`, async ({ request }) => {
    const tripKey = await getTripKeyFromRequest(request);
    return HttpResponse.json(createTripStatusResponse("Confirmed", tripKey), { status: 200 });
  }),
];

const getTripStatus404Handler = [
  http.post(`**${apiPaths.trips.status}`, async () => {
    return HttpResponse.json({}, { status: 404 });
  }),
];

const getTripStatusLoadingHandler = [
  http.post(`**${apiPaths.trips.status}`, async ({ request }) => {
    await waitUntilRequestAborted(request);
    return HttpResponse.json(createTripStatusResponse("Confirmed", "loading-trip-key"), { status: 200 });
  }),
];

export const getTripStatusHandlers = {
  get200Confirmed: getConfirmedTripStatusHandler,
  get200InProcess: getInProcessTripStatusHandler,
  get200InAttempt: getInAttemptTripStatusHandler,
  get200ConfirmedAfterPolling: getConfirmedTripStatusAfterPollingHandler,
  get404: getTripStatus404Handler,
  getLoading: getTripStatusLoadingHandler,
} as const;

export const getTripStatusHandlersFactory = {
  create200: (status: Trips_Status_TripStatus, options?: GetTripStatusHandlerFactoryOptions) => {
    return createGetTripStatus200Handler(status, options);
  },
} as const;
